<template>
  <TransitionRoot as="template" :show="open">
    <Dialog class="relative z-50" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div class="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" class="text-lg font-semibold leading-6 text-gray-900">Ajuste Manual de Movimentação</DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">
                      Alteração manual de valores para o segurado <span class="font-bold text-gray-700">{{ movimentacao?.segurado_nome }}</span>.
                      Esta ação será registrada para auditoria.
                    </p>
                  </div>
                </div>
              </div>

              <div class="mt-6 space-y-4">
                <!-- Seleção do Campo -->
                <div>
                  <label for="campo" class="block text-sm font-medium text-gray-700">Campo para Ajuste</label>
                  <select id="campo" v-model="form.campo" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm">
                    <option value="valor_capital_novo">Capital Novo</option>
                    <option value="valor_mensal_novo">Mensal Novo</option>
                    <option value="valor_premio_novo">Prêmio Novo</option>
                  </select>
                </div>

                <!-- Valor Atual (informativo) -->
                <div>
                  <label class="block text-sm font-medium text-gray-700">Valor Atual</label>
                  <div class="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600 font-mono">
                    {{ formatCurrency(valorAtual) }}
                  </div>
                </div>

                <!-- Novo Valor -->
                <div>
                  <label for="valor_novo" class="block text-sm font-medium text-gray-700">Novo Valor</label>
                  <div class="relative mt-1 rounded-md shadow-sm">
                    <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <span class="text-gray-500 sm:text-sm">R$</span>
                    </div>
                    <input type="number" step="0.01" v-model="form.valor_novo" id="valor_novo" class="block w-full rounded-md border-gray-300 pl-10 focus:border-amber-500 focus:ring-amber-500 sm:text-sm" placeholder="0,00" />
                  </div>
                </div>

                <!-- Justificativa -->
                <div>
                  <label for="justificativa" class="block text-sm font-medium text-gray-700">Justificativa (obrigatória)</label>
                  <textarea id="justificativa" v-model="form.justificativa" rows="3" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm" placeholder="Explique o motivo do ajuste manual..."></textarea>
                </div>
              </div>

              <div class="mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button type="button" :disabled="loading" @click="confirmarAjuste" class="inline-flex w-full justify-center rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600 sm:col-start-2 disabled:opacity-50 transition-colors">
                  {{ loading ? 'Processando...' : 'Confirmar Ajuste' }}
                </button>
                <button type="button" @click="closeDialog" class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0 transition-colors">
                  Cancelar
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import ControleMovimentosRepository from '@/services/ControleMovimentosRepository.vue';
import Swal from 'sweetalert2';

export default {
  name: 'AjusteManualModal',
  components: {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot
  },
  props: {
    open: {
      type: Boolean,
      required: true
    },
    movimentacao: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      form: {
        campo: 'valor_mensal_novo',
        valor_novo: null,
        justificativa: ''
      }
    };
  },
  computed: {
    valorAtual() {
      if (!this.movimentacao || !this.form.campo) return 0;
      return this.movimentacao[this.form.campo] || 0;
    }
  },
  watch: {
    open(val) {
      if (val) {
        this.resetForm();
      }
    },
    'form.campo'(newVal) {
      // Opcional: pré-preecher o novo valor com o atual para facilitar
      if (this.movimentacao) {
        this.form.valor_novo = this.movimentacao[newVal];
      }
    }
  },
  methods: {
    closeDialog() {
      this.$emit('update:open', false);
    },
    resetForm() {
      this.form.campo = 'valor_mensal_novo';
      this.form.valor_novo = this.movimentacao ? this.movimentacao['valor_mensal_novo'] : null;
      this.form.justificativa = '';
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    },
    async confirmarAjuste() {
      if (!this.form.valor_novo && this.form.valor_novo !== 0) {
        Swal.fire('Atenção', 'Informe o novo valor.', 'warning');
        return;
      }
      if (!this.form.justificativa || this.form.justificativa.length < 5) {
        Swal.fire('Atenção', 'A justificativa é obrigatória e deve ter pelo menos 5 caracteres.', 'warning');
        return;
      }

      this.loading = true;
      const repo = new ControleMovimentosRepository();
      const user = JSON.parse(window.localStorage.getItem("myUser"));

      try {
        const payload = {
          ...this.form,
          id: this.movimentacao.id
        };
        const response = await repo.ajustarMovimentacao(user.token, this.movimentacao.id, payload);
        
        if (response.data.status) {
          Swal.fire('Sucesso!', 'Ajuste manual realizado com sucesso.', 'success');
          this.$emit('sucesso', response.data.data);
          this.closeDialog();
        } else {
          Swal.fire('Erro', response.data.message || 'Falha ao processar ajuste.', 'error');
        }
      } catch (error) {
        console.error("Erro no ajuste manual:", error);
        const msg = error.response?.data?.message || 'Erro ao conectar com o servidor.';
        Swal.fire('Erro', msg, 'error');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
